import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";
import User from "../../models/User";

function ModalHeaderPro({ title, onHide }) {
    return (
        <div className="users-admin-modal__header">
            <div className="users-admin-modal__header-main">
                <h5 className="users-admin-modal__title mb-0">{title}</h5>
            </div>
            <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
        </div>
    );
}

/** تسمية يمين الحقل ومربع الإدخال يسارها (RTL) — سطر واحد مضغوط */
function InlineField({ label, htmlFor, children }) {
    return (
        <div className="users-admin-modal__inline-field">
            <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">{label}</Form.Label>
            <div className="users-admin-modal__inline-control">{children}</div>
        </div>
    );
}

function CreateUserModal({ show, onHide, onSaved }) {
    const { backEnd_request, governorates, UserTypes } = BackMethods();
    const [roles, setRoles] = useState([]);
    const [user, setUser] = useState(() => new User());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!show) return;
        setUser(new User());
        setFormError("");
        setShowPassword(false);
    }, [show]);

    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await backEnd_request.get("/admin/Roles");
                if (!cancelled) setRoles(res.data || []);
            } catch {
                if (!cancelled) setRoles([]);
            }
        })();
        return () => { cancelled = true; };
    }, [show, backEnd_request]);

    const handleChange = (field, value) => {
        setUser((prev) => new User({
            ...prev,
            [field]: field === "roleId" || field === "userType" ? Number(value) || 0 : value,
        }));
    };

    const validate = () => {
        if (!user.username?.trim()) return "الرجاء إدخال اسم المستخدم";
        if (!user.firstName?.trim()) return "الرجاء إدخال الاسم الأول";
        if (!user.phoneNumber?.trim()) return "الرجاء إدخال رقم الهاتف";
        if (!user.initialPassword?.trim()) return "الرجاء إدخال كلمة المرور الابتدائية";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");
        const error = validate();
        if (error) {
            setFormError(error);
            return;
        }
        setIsSubmitting(true);
        try {
            await backEnd_request.post("/admin/UsersManagement", user.userToCreateAccountAPI());
            onSaved();
            onHide();
        } catch (err) {
            setFormError(err.response?.data?.message || "تعذر إنشاء الحساب");
        } finally {
            setIsSubmitting(false);
        }
    };

    const pwdControl = (
        <div className="position-relative">
            <Form.Control
                id="create-pwd"
                size="sm"
                className="rounded-2"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={user.initialPassword || ""}
                onChange={(e) => handleChange("initialPassword", e.target.value)}
                style={{ paddingLeft: "2rem" }}
            />
            <button type="button" className="btn btn-link position-absolute top-50 start-0 translate-middle-y text-secondary p-0 ps-1 border-0" onClick={() => setShowPassword(!showPassword)} tabIndex={-1} aria-label={showPassword ? "إخفاء" : "إظهار"}>
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} small`} />
            </button>
        </div>
    );

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="users-admin-modal-dialog" className="users-admin-modal users-admin-modal--compact" dir="rtl" backdrop="static">
            <ModalHeaderPro title="إضافة مستخدم" onHide={onHide} />
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <p className="users-admin-modal__hint">
                        <i className="bi bi-person-plus"></i>
                        <span>أضف حساباً جديداً مع بيانات دقيقة، يمكن تعديل معظم الحقول لاحقاً من شاشة إدارة المستخدمين.</span>
                    </p>
                    <Row className="g-2 gx-lg-3">
                        <Col lg={6}>
                            <InlineField label="اسم المستخدم" htmlFor="create-username">
                                <Form.Control size="sm" className="rounded-2" id="create-username" autoComplete="username" value={user.username || ""} onChange={(e) => handleChange("username", e.target.value)} />
                            </InlineField>
                            <InlineField label="اسم الأب" htmlFor="create-father">
                                <Form.Control size="sm" className="rounded-2" id="create-father" value={user.fatherName || ""} onChange={(e) => handleChange("fatherName", e.target.value)} />
                            </InlineField>
                            <InlineField label="الهاتف" htmlFor="create-phone">
                                <Form.Control size="sm" className="rounded-2" id="create-phone" type="tel" dir="ltr" style={{ textAlign: "right" }} value={user.phoneNumber || ""} onChange={(e) => handleChange("phoneNumber", e.target.value)} />
                            </InlineField>
                            <InlineField label="المدينة" htmlFor="create-city">
                                <Form.Control size="sm" className="rounded-2" id="create-city" value={user.city || ""} onChange={(e) => handleChange("city", e.target.value)} />
                            </InlineField>
                            <InlineField label="نوع المستخدم" htmlFor="create-utype">
                                <Form.Select size="sm" className="rounded-2" id="create-utype" value={user.userType ? String(user.userType) : ""} onChange={(e) => handleChange("userType", e.target.value === "" ? 0 : parseInt(e.target.value, 10))}>
                                    <option value="">—</option>
                                    {UserTypes.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </Form.Select>
                            </InlineField>
                            <InlineField label="كلمة المرور" htmlFor="create-pwd">
                                {pwdControl}
                            </InlineField>
                        </Col>
                        <Col lg={6}>
                            <InlineField label="الاسم الأول" htmlFor="create-first">
                                <Form.Control size="sm" className="rounded-2" id="create-first" value={user.firstName || ""} onChange={(e) => handleChange("firstName", e.target.value)} />
                            </InlineField>
                            <InlineField label="الكنية" htmlFor="create-last">
                                <Form.Control size="sm" className="rounded-2" id="create-last" value={user.lastName || ""} onChange={(e) => handleChange("lastName", e.target.value)} />
                            </InlineField>
                            <InlineField label="المحافظة" htmlFor="create-gov">
                                <Form.Select size="sm" className="rounded-2" id="create-gov" value={user.governorate || ""} onChange={(e) => handleChange("governorate", e.target.value)}>
                                    <option value="">—</option>
                                    {governorates.map((g) => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </Form.Select>
                            </InlineField>
                            <InlineField label="القطاع" htmlFor="create-sector">
                                <Form.Control size="sm" className="rounded-2" id="create-sector" value={user.sector || ""} onChange={(e) => handleChange("sector", e.target.value)} />
                            </InlineField>
                            <InlineField label="الصلاحية" htmlFor="create-role">
                                <Form.Select size="sm" className="rounded-2" id="create-role" value={user.roleId || 0} onChange={(e) => handleChange("roleId", e.target.value)}>
                                    <option value={0}>—</option>
                                    {roles.map((role) => (
                                        <option key={role.roleId} value={role.roleId}>{role.displayName}</option>
                                    ))}
                                </Form.Select>
                            </InlineField>
                            <div className="users-admin-modal__inline-field users-admin-modal__inline-field--check">
                                <span className="users-admin-modal__inline-label users-admin-modal__inline-label--spacer" aria-hidden> </span>
                                <div className="users-admin-modal__inline-control">
                                    <Form.Check id="mustChangePwdCreate" className="small" type="checkbox" label="تغيير كلمة المرور عند أول دخول" checked={user.mustChangePassword} onChange={(e) => handleChange("mustChangePassword", e.target.checked)} />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {formError ? <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">{formError}</div> : null}

                    <div className="users-admin-modal__footer d-flex gap-2 justify-content-end">
                        <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>إلغاء</Button>
                        <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <><Spinner animation="border" size="sm" className="ms-1" /> حفظ</> : "إنشاء الحساب"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

function EditUserModal({ show, userId, onHide, onSaved }) {
    const { backEnd_request, governorates } = BackMethods();
    const [roles, setRoles] = useState([]);
    const [user, setUser] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    useEffect(() => {
        if (!show || !userId) return;
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            setFormError("");
            try {
                const [userRes, rolesRes] = await Promise.all([
                    backEnd_request.get(`/admin/UsersManagement/${userId}`),
                    backEnd_request.get("/admin/Roles"),
                ]);
                if (!cancelled) {
                    setUser(userRes.data || {});
                    setRoles(rolesRes.data || []);
                }
            } catch {
                if (!cancelled) {
                    setFormError("تعذر تحميل بيانات المستخدم");
                    setUser({});
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [show, userId, backEnd_request]);

    const handleChange = (field, value) => {
        setUser((prev) => ({
            ...prev,
            [field]: field === "roleId" ? Number(value) || 0 : value,
        }));
    };

    const validate = () => {
        if (!user.firstName?.trim()) return "الرجاء إدخال الاسم الأول";
        if (!user.phoneNumber?.trim()) return "الرجاء إدخال رقم الهاتف";
        if (!user.roleId) return "الرجاء اختيار صلاحية المستخدم";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");
        const error = validate();
        if (error) {
            setFormError(error);
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                firstName: user.firstName,
                fatherName: user.fatherName,
                lastName: user.lastName,
                phoneNumber: user.phoneNumber,
                governorate: user.governorate,
                city: user.city,
                sector: user.sector,
                roleId: user.roleId,
            };
            await backEnd_request.post(`/admin/UsersManagement/${userId}`, payload);
            onSaved();
            onHide();
        } catch (err) {
            setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="users-admin-modal-dialog" className="users-admin-modal users-admin-modal--compact" dir="rtl" backdrop="static">
            <ModalHeaderPro title="تعديل مستخدم" onHide={onHide} />
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        <p className="users-admin-modal__hint">
                            <i className="bi bi-pencil-square"></i>
                            <span>حدّث بيانات هذا المستخدم مع الحفاظ على حسابه وصلاحياته، بدون تغيير كلمة المرور.</span>
                        </p>
                        {user.username ? (
                            <div className="users-admin-modal__user-chip mb-2 py-1">
                                <i className="bi bi-person-badge text-secondary small" />
                                <span className="text-muted" style={{ fontSize: "0.75rem" }}>المستخدم</span>
                                <strong className="small">{user.username}</strong>
                            </div>
                        ) : null}

                        <Row className="g-2 gx-lg-3">
                            <Col lg={6}>
                                <InlineField label="الاسم الأول" htmlFor="edit-first">
                                    <Form.Control size="sm" className="rounded-2" id="edit-first" value={user.firstName || ""} onChange={(e) => handleChange("firstName", e.target.value)} />
                                </InlineField>
                                <InlineField label="الكنية" htmlFor="edit-last">
                                    <Form.Control size="sm" className="rounded-2" id="edit-last" value={user.lastName || ""} onChange={(e) => handleChange("lastName", e.target.value)} />
                                </InlineField>
                                <InlineField label="الهاتف" htmlFor="edit-phone">
                                    <Form.Control size="sm" className="rounded-2" id="edit-phone" type="tel" dir="ltr" style={{ textAlign: "right" }} value={user.phoneNumber || ""} onChange={(e) => handleChange("phoneNumber", e.target.value)} />
                                </InlineField>
                                <InlineField label="المدينة" htmlFor="edit-city">
                                    <Form.Control size="sm" className="rounded-2" id="edit-city" value={user.city || ""} onChange={(e) => handleChange("city", e.target.value)} />
                                </InlineField>
                            </Col>
                            <Col lg={6}>
                                <InlineField label="اسم الأب" htmlFor="edit-father">
                                    <Form.Control size="sm" className="rounded-2" id="edit-father" value={user.fatherName || ""} onChange={(e) => handleChange("fatherName", e.target.value)} />
                                </InlineField>
                                <InlineField label="المحافظة" htmlFor="edit-gov">
                                    <Form.Select size="sm" className="rounded-2" id="edit-gov" value={user.governorate || ""} onChange={(e) => handleChange("governorate", e.target.value)}>
                                        <option value="">—</option>
                                        {governorates.map((g) => (
                                            <option key={g} value={g}>{g}</option>
                                        ))}
                                    </Form.Select>
                                </InlineField>
                                <InlineField label="القطاع" htmlFor="edit-sector">
                                    <Form.Control size="sm" className="rounded-2" id="edit-sector" value={user.sector || ""} onChange={(e) => handleChange("sector", e.target.value)} />
                                </InlineField>
                                <InlineField label="الصلاحية" htmlFor="edit-role">
                                    <Form.Select size="sm" className="rounded-2" id="edit-role" value={user.roleId || 0} onChange={(e) => handleChange("roleId", e.target.value)}>
                                        <option value={0}>—</option>
                                        {roles.map((role) => (
                                            <option key={role.roleId} value={role.roleId}>{role.displayName}</option>
                                        ))}
                                    </Form.Select>
                                </InlineField>
                            </Col>
                        </Row>

                        {formError ? <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">{formError}</div> : null}

                        <div className="users-admin-modal__footer d-flex gap-2 justify-content-end">
                            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>إلغاء</Button>
                            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? <><Spinner animation="border" size="sm" className="ms-1" /> حفظ</> : "حفظ التغييرات"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
}

function ChangePasswordUserModal({ show, userId, username, onHide, onSaved }) {
    const { backEnd_request } = BackMethods();
    const [newPassword, setNewPassword] = useState("");
    const [mustChangePassword, setMustChangePassword] = useState(true);
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!show) return;
        setNewPassword("");
        setMustChangePassword(true);
        setMessage("");
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setIsSubmitting(true);
        try {
            await backEnd_request.post(`/admin/UsersManagement/${userId}/change-password`, {
                newPassword,
                mustChangePassword,
            });
            onSaved();
            onHide();
        } catch (error) {
            setMessage(error.response?.data?.message || "تعذر تغيير كلمة المرور");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="users-admin-modal-dialog users-admin-modal-dialog--narrow" className="users-admin-modal users-admin-modal--compact" dir="rtl" backdrop="static">
            <ModalHeaderPro title="تغيير كلمة المرور" onHide={onHide} />
            <Modal.Body>
                {username ? (
                    <div className="users-admin-modal__user-chip mb-2 py-1">
                        <i className="bi bi-person-badge text-secondary small" />
                        <strong className="small">{username}</strong>
                    </div>
                ) : null}
                <p className="users-admin-modal__hint mb-2">
                    <i className="bi bi-shield-lock"></i>
                    <span>اختر كلمة مرور قوية وسهلة التذكّر، ويمكنك إجبار المستخدم على تغييرها عند أول دخول.</span>
                </p>
                {message ? <div className="alert alert-danger py-1 px-2 small mb-2" role="alert">{message}</div> : null}
                <Form onSubmit={handleSubmit}>
                    <InlineField label="كلمة المرور" htmlFor="pwd-new">
                        <Form.Control size="sm" className="rounded-2" id="pwd-new" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={1} />
                    </InlineField>
                    <div className="users-admin-modal__inline-field users-admin-modal__inline-field--check mt-1">
                        <span className="users-admin-modal__inline-label users-admin-modal__inline-label--spacer" aria-hidden> </span>
                        <div className="users-admin-modal__inline-control">
                            <Form.Check id="mustChPwdModal" className="small" type="checkbox" label="إجبار التغيير عند الدخول" checked={mustChangePassword} onChange={() => setMustChangePassword(!mustChangePassword)} />
                        </div>
                    </div>
                    <div className="users-admin-modal__footer d-flex gap-2 justify-content-end">
                        <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>إلغاء</Button>
                        <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : "تطبيق"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default function UserManagementModals({ modal, userId, passwordUsername, onClose, onSaved }) {
    return (
        <>
            <CreateUserModal show={modal === "create"} onHide={onClose} onSaved={onSaved} />
            <EditUserModal show={modal === "edit"} userId={userId} onHide={onClose} onSaved={onSaved} />
            <ChangePasswordUserModal show={modal === "password"} userId={userId} username={passwordUsername} onHide={onClose} onSaved={onSaved} />
        </>
    );
}
